// Centralized PostHog event helpers for the recap flow.
//
// All recap analytics route through this module so:
//   - Event names live in one place — rename = single-file change
//   - Property shapes are consistent across components
//   - The league_id_hash session-level super-property is registered once
//     and automatically attached to every subsequent event
//   - posthog-js is imported once, not per-component
//
// PostHog itself is initialized in src/App.js with the project key.

import posthog from "posthog-js";

// ----------------------------------------------------------------------------
// Session-level league identity (SHA-256, first 8 hex chars)
// ----------------------------------------------------------------------------
// Funnel events use a hash, not the raw id — an 8-char prefix of SHA-256 gives
// ~4 billion buckets, enough to track per-league funnels without turning the
// analytics store into a directory of externally addressable leagues.
//
// EXCEPTION: captureSleeperLeagueCaptured (below) deliberately sends the RAW
// Sleeper league_id, because the whole point of that event is to be able to go
// back to Sleeper's public API for draft results. Note the consequence: the
// hash is a session super-property, so any league emitting that event pairs its
// raw id with its hash and is no longer protected by the scheme above.
// Same exception, same reasoning for captureSleeperUserLookedUp: raw ids for
// EVERY league the user is in, captured at lookup even if they import none.

export async function registerLeagueHash(leagueIdOrKey) {
  if (!leagueIdOrKey) return null;
  try {
    const data = new TextEncoder().encode(String(leagueIdOrKey));
    const hashBuf = await crypto.subtle.digest("SHA-256", data);
    const hex = Array.from(new Uint8Array(hashBuf).slice(0, 4))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
    // Becomes a super-property on every subsequent event in this session.
    posthog.register({ league_id_hash: hex });
    return hex;
  } catch {
    return null;
  }
}

// ----------------------------------------------------------------------------
// FUNNEL EVENTS (conversion sequence)
// ----------------------------------------------------------------------------

export function captureLandingView() {
  posthog.capture("recap_landing_view");
}

export function capturePlatformSelected(platform) {
  posthog.capture("recap_platform_selected", { platform });
}

export function captureLeagueImported({ platform, leagueIdHash, seasonCount, ownerCount, fetchDurationMs }) {
  posthog.capture("recap_league_imported", {
    platform,
    league_id_hash: leagueIdHash,
    season_count: seasonCount,
    owner_count: ownerCount,
    fetch_duration_ms: fetchDurationMs,
  });
}

export function captureEmailSubmitted({ email, crossPromoOptedIn }) {
  posthog.capture("recap_email_submitted", {
    email_domain: extractDomain(email),
    cross_promo_opted_in: !!crossPromoOptedIn,
  });
}

export function captureUnlockClicked() {
  posthog.capture("recap_unlock_clicked");
}

export function captureTriviaGenerated({ model, latencyMs, bulletCount, sectionsWithBullets, confidenceScore, cacheHit }) {
  posthog.capture("recap_trivia_generated", {
    model,
    latency_ms: latencyMs,
    bullet_count: bulletCount,
    sections_with_bullets: sectionsWithBullets,
    confidence_score: confidenceScore,
    cache_hit: !!cacheHit,
  });
}

// ----------------------------------------------------------------------------
// ENGAGEMENT EVENTS (which bullets resonate — most important post-launch signal)
// ----------------------------------------------------------------------------

export function captureBulletCopied({ section, bulletIndex, confidenceScore }) {
  posthog.capture("recap_bullet_copied", {
    section,
    bullet_index: bulletIndex,
    confidence_score: confidenceScore,
  });
}

export function captureCardUrlCopied({ cardType, bulletIdIfApplicable }) {
  posthog.capture("recap_card_url_copied", {
    card_type: cardType,
    bullet_id_if_applicable: bulletIdIfApplicable ?? null,
  });
}

export function captureCardDownloaded({ cardType, bulletIdIfApplicable }) {
  posthog.capture("recap_card_downloaded", {
    card_type: cardType,
    bullet_id_if_applicable: bulletIdIfApplicable ?? null,
  });
}

export function captureSectionView(sectionName) {
  posthog.capture("recap_section_view", { section_name: sectionName });
}

// A2 lineup detail Phase 1. `source` distinguishes pre-pulled record-game
// expansions (instant) from lazy fetches against an uncached non-record
// game (~1.1s on Yahoo). Lets us measure adoption + cache hit rate.
export function captureAlmanacLineupViewed({ year, week, owner, source }) {
  posthog.capture("almanac_lineup_viewed", {
    year: year || null,
    week: week || null,
    owner: owner || null,
    source: source || null,
  });
}

// Task #41 — JSON download. Tracks adoption of the Almanac-as-archive
// promise + which leagues are exporting (volume + size + platform mix).
export function captureAlmanacJsonDownloaded({ platform, seasonCount, fileSizeKb }) {
  posthog.capture("almanac_json_downloaded", {
    platform: platform || null,
    season_count: seasonCount || 0,
    file_size_kb: fileSizeKb || 0,
  });
}

// JSON re-import — the other half of the archive promise. Post-Yahoo-403
// this is the only door back into a Yahoo report, so success volume ≈ how
// many stranded leagues the download actually saved. Mirror properties of
// the download event so the two funnels compare directly.
export function captureAlmanacJsonLoaded({ platform, seasonCount, ownerCount, fileSizeKb }) {
  posthog.capture("almanac_json_loaded", {
    platform: platform || null,
    season_count: seasonCount || 0,
    owner_count: ownerCount || 0,
    file_size_kb: fileSizeKb || 0,
  });
}

// reason is the user-facing message from AlmanacImportError — deliberately,
// because it names WHICH validation tripped (bad JSON / wrong schema /
// missing stats), which is exactly the triage question.
export function captureAlmanacJsonLoadFailed({ reason, fileSizeKb }) {
  posthog.capture("almanac_json_load_failed", {
    reason: reason || null,
    file_size_kb: fileSizeKb || 0,
  });
}

// Share links — the third door onto the same archive (download / re-import /
// share). Property names deliberately match almanac_json_downloaded so the
// three funnels compare directly.
//
// 🔴 file_size_kb IS PINNED TO THE MINIFIED PAYLOAD THE CLIENT POSTED, BY
// DESIGN — the same basis almanac_json_downloaded uses, and that comparability
// is the property's entire purpose. The server also knows a size (its stored
// base64, ~6x smaller) and may one day return it; taking that when present
// would silently change the basis MID-SERIES and leave a chart nobody can
// interpret. The caller deliberately ignores any server-reported size. If a
// compressed-size metric is ever wanted, it needs its OWN property name.
//
// Never the raw share id: the id IS the capability, and PostHog is not where
// it belongs.
export function captureAlmanacSharePublished({ platform, seasonCount, sizeKb }) {
  posthog.capture("almanac_share_published", {
    platform: platform || null,
    season_count: seasonCount || 0,
    file_size_kb: sizeKb || 0,
  });
}

// reason is the user-facing message, same rationale as the load-failed event:
// it names WHICH thing tripped (too large / bad shape / rate limited / network
// down), which is the triage question. The 413 copy is the one we most want to
// see fire — it is the only cap we guessed at rather than measured.
export function captureAlmanacShareFailed({ reason, sizeKb }) {
  posthog.capture("almanac_share_failed", {
    reason: (reason || "").slice(0, 200),
    file_size_kb: sizeKb || 0,
  });
}

// The VIEWER side — someone who opened /almanac?share=<id>. Fires once per
// successful shared render. These people are the growth surface: they arrive
// as non-users looking at a league they're in, which is why the shared view
// carries the "build your own" banner and skips the email gate.
//
// 🔴 NO SHARE ID, EVER. The id is the read capability for a payload carrying
// real people's names; putting it in an analytics event would park a working
// key in a third-party system. If a per-link dimension is ever needed, hash it
// the way registerLeagueHash does.
export function captureAlmanacShareViewed({ platform, seasonCount }) {
  posthog.capture("almanac_share_viewed", {
    platform: platform || null,
    season_count: seasonCount || 0,
  });
}

// reason is the user-facing message, same as the other failure events — it
// separates "mistyped/removed link" from "the export we stored won't parse",
// which are very different problems.
export function captureAlmanacShareViewFailed({ reason }) {
  posthog.capture("almanac_share_view_failed", {
    reason: (reason || "").slice(0, 200),
  });
}

// A1 Patch C — fetch telemetry. Measures real-world elapsed time and
// cache hit rate so we can validate the IndexedDB cache (Patch D) is
// paying off + see Yahoo rate-limit pain in the wild.
export function captureAlmanacFetchCompleted({ platform, seasonCount, elapsedSeconds, leagueName, cacheHits, cacheMisses }) {
  posthog.capture("almanac_fetch_completed", {
    platform: platform || null,
    season_count: seasonCount || 0,
    elapsed_seconds: elapsedSeconds || 0,
    league_name: leagueName || null,
    cache_hits: cacheHits || 0,
    cache_misses: cacheMisses || 0,
  });
}

export function captureAlmanacFetchFailed({ platform, seasonCount, elapsedSoFar, errorReason }) {
  posthog.capture("almanac_fetch_failed", {
    platform: platform || null,
    season_count: seasonCount || 0,
    elapsed_so_far: elapsedSoFar || 0,
    error_reason: (errorReason || "").slice(0, 200),
  });
}

// ----------------------------------------------------------------------------
// ERROR EVENTS
// ----------------------------------------------------------------------------

export function captureTriviaFailed({ errorCode, errorMessage, model, fallbackDisplayed }) {
  posthog.capture("recap_trivia_failed", {
    error_code: errorCode || null,
    error_message: (errorMessage || "").slice(0, 200), // bound payload size
    model: model || null,
    fallback_displayed: !!fallbackDisplayed,
  });
}

export function captureFetchFailed({ platform, stage, errorMessage }) {
  posthog.capture("recap_fetch_failed", {
    platform,
    stage,
    error_message: (errorMessage || "").slice(0, 200),
  });
}

// ----------------------------------------------------------------------------
// DATA ACQUISITION
// ----------------------------------------------------------------------------

// Records the RAW Sleeper league_id every time one passes through the app, so
// public draft results and history can be pulled from Sleeper's API later —
// real auction rooms are the calibration bottleneck. Sleeper league ids are
// public identifiers; see the EXCEPTION note at the top of this file.
//
// previous_league_id chains a league to its prior season, which is what makes
// multi-year history reachable from a single capture. It is passed only when a
// caller already has it — never worth an extra request.
export function captureSleeperLeagueCaptured({ leagueId, leagueName, season, source, previousLeagueId }) {
  if (!leagueId) return;
  try {
    posthog.capture("sleeper_league_captured", {
      league_id: String(leagueId),
      league_name: leagueName || null,
      season: season ? String(season) : null,
      source: source || null,
      previous_league_id: previousLeagueId ? String(previousLeagueId) : null,
    });
  } catch {
    /* analytics must never break an import */
  }
}

// Fires the moment a Sleeper username resolves to a league list — BEFORE the
// user picks one to import. Most lookups end there, so this is where the bulk
// of the reachable-league surface lives: one lookup exposes every league that
// person is in, not just the one they went on to import. Raw ids by design
// (see the EXCEPTION note at the top of this file).
export function captureSleeperUserLookedUp({ username, userId, season, leagues }) {
  try {
    const list = Array.isArray(leagues) ? leagues : [];
    posthog.capture("sleeper_user_looked_up", {
      username: username || null,
      sleeper_user_id: userId ? String(userId) : null,
      season: season ? String(season) : null,
      league_count: list.length,
      league_ids: list.map((l) => String(l.league_id)),
      league_names: list.map((l) => l.name || null),
    });
  } catch {
    /* analytics must never break a lookup */
  }
}

// ----------------------------------------------------------------------------
// ESPN DRAFT SHAPE (C5b)
// ----------------------------------------------------------------------------

// NO REAL ESPN AUCTION HAS EVER BEEN THROUGH THIS PIPELINE. Every ESPN parsing
// decision — the 53-not-41 PPR key, the additive→partition fumble conversion,
// FLEX generalization, the position map that drops punters and IDP — was
// derived from ONE sampled league and a provenance doc. The first real import
// is the first evidence any of it holds, so it has to be observable when it
// lands, not reconstructed afterwards from a support email.
//
// SHAPE ONLY, same posture as anonymizeAuctionHistory: no player names, no
// league name, no league id, no team or owner names. Everything here is a
// count, a flag, or a dollar total. Season YEARS are included because a
// reconciliation you cannot place in time is not actionable — a year is not an
// identifier, and it is already carried by every other almanac event.
//
// ⚠️ DELIBERATELY NOT captureAlmanacFetchCompleted, which sends league_name.
// This event is the one that will be read while debugging a stranger's import,
// so it carries nothing that names them.
//
// THE RECONCILIATION IS THE POINT. Total spend should land on budget × teams in
// a completed auction. It won't always — unspent money is real, and keepers
// legitimately depress it — but a season coming back at 40% of expected means
// something upstream is wrong (dropped picks, a misparsed budget, a snake
// league misflagged as AUCTION), and the ratio is what makes that visible
// without ever looking at the league itself.
export function captureEspnDraftShape({ seasonCount, totalPicks, picksDropped, anyAuction, perSeason }) {
  try {
    posthog.capture("espn_draft_shape", {
      season_count: seasonCount || 0,
      total_picks: totalPicks || 0,
      picks_dropped: picksDropped || 0,
      any_auction: !!anyAuction,
      auction_season_count: (perSeason || []).filter((s) => s.is_auction).length,
      per_season: perSeason || [],
    });
  } catch {
    /* analytics must never break an import */
  }
}

// Builds the per-season reconciliation rows from parsed ESPN seasons. Exported
// and pure so the shape can be reasoned about (and tested) without a PostHog
// client in the way.
//
// `pct_of_expected` is null rather than 0 when there is nothing to divide by —
// a non-auction league has no meaningful budget × teams, and a 0 there would
// pollute every average taken over this field.
export function buildEspnSeasonShape(seasons) {
  return (Array.isArray(seasons) ? seasons : []).map((s) => {
    const picks = Array.isArray(s?.picks) ? s.picks : [];
    const spend = picks.reduce((sum, p) => sum + (Number(p?.amount) || 0), 0);
    const teams = Number(s?.teams) || 0;
    const budget = Number(s?.budget) || 0;
    const expected = s?.is_auction_draft ? budget * teams : 0;
    return {
      season: s?.season ?? null,
      is_auction: !!s?.is_auction_draft,
      teams,
      budget,
      pick_count: picks.length,
      dropped_picks: Number(s?.droppedPicks) || 0,
      // Keepers land at their carried price and $0 picks are legal, so this is
      // a total, not a proxy for pick count.
      total_spend: spend,
      expected_spend: expected,
      pct_of_expected: expected > 0 ? Math.round((spend / expected) * 100) : null,
      // Roster shape rides along because a wrong slot parse shows up here
      // first — an ESPN slot Mock1 generalized into FLEX changes this count.
      roster_slots: s?.positions ? Object.keys(s.positions).length : 0,
      note_count: Array.isArray(s?.notes) ? s.notes.length : 0,
    };
  });
}

// ----------------------------------------------------------------------------
// Helpers
// ----------------------------------------------------------------------------

function extractDomain(email) {
  if (!email || typeof email !== "string") return null;
  const at = email.lastIndexOf("@");
  if (at < 0) return null;
  return email.slice(at + 1).toLowerCase();
}
